import React, { memo, useRef, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';

import Table, { Row } from '../../helpers/TableToAgGridTable';
import { convertDateString } from '../../../utils/dateFormat';
import TextEditor from '../../helpers/TextEditor';
import { GridCheckbox } from '../../helpers/GridCheckbox';
import config from '../../../config';
import { IconButton } from '../../helpers/IconButton';
import Button from '../../helpers/Button';

import style from './style.module.css';

const COLOR_RED = '#ff0000';
const COLOR_GREEN = '#008000';
const ACTIONS_COLUMN_WIDTH = 34;
const TCPTitle =
  'This is the date associated with the last Approval List. Dates in Green mean this is the date the last list was sent, but it has not yet been returned. Dates in Red is the date the last Approval List was returned.';

function configColumnsMap(name) {
  const configColumns = config.main.getIn(['widgets', name, 'columns'], []);

  return configColumns.reduce((accumulator, column) => accumulator.set(column.get('field'), column), Map());
}

function getColumnsTCP({ columns }) {
  return columns.map(row => ({
    ...row.toJS(),
    cellStyle(params) {
      if (params.column.colId === 'date') {
        const color = params.data.listType === 'green' ? COLOR_GREEN : COLOR_RED;

        return { color };
      }

      return null;
    },
    cellRenderer(params) {
      if (params.column.colId === 'date') {
        return `<span title='${TCPTitle}'>${params.value}</span>`;
      }

      if (params.column.colId === 'percentOfContacted') {
        if (params.value === '0') {
          return '';
        }

        return `<span>${params.data.contactedCount}(${params.value})</span>`;
      }

      return `<span>${params.value}</span>`;
    },
  }));
}

function getColumnsBacklog({ columns }) {
  return columns.map(row => ({
    ...row.toJS(),
    cellStyle(params) {
      if (params.data.periodType === 'future') {
        return { color: COLOR_GREEN };
      }

      if (params.data.periodType === 'past') {
        return { color: COLOR_RED };
      }
    },
    cellRenderer(params) {
      return `<span>${params.value}</span>`;
    },
  }));
}

const DoubleClickableCell = ({ params, children }) => (
  <span
    onDoubleClick={event => {
      event.stopPropagation();
      params.api.dispatchEvent({
        type: 'cellDoubleClicked',
        ...params,
      });
    }}
  >
    {children}
  </span>
);

function getColumnsMeetings({ columns, onDeleteMeeting, onViewMeetingDetailsNote }) {
  return columns.map(row => ({
    ...row.toJS(),
    ...(row.get('field') === 'actions' && {
      width: ACTIONS_COLUMN_WIDTH,
      minWidth: ACTIONS_COLUMN_WIDTH,
      maxWidth: ACTIONS_COLUMN_WIDTH,
    }),
    cellClass(params) {
      if (params.colDef.field === 'actions') {
        return style.actionsCell;
      }
    },
    cellRendererFramework(params) {
      if (params.colDef.field === 'meetingDateTime' || params.colDef.field === 'expiresAt') {
        return convertDateString(params.value, 'MM/DD/YY hh:mma');
      }

      if (params.colDef.field === 'meetingLink') {
        if (!params.data.hasSummaryHtml) {
          return <Button className="btn-disabled btn-link c-black">N/A</Button>;
        }

        return (
          <Button className="btn-link" onClick={() => onViewMeetingDetailsNote(params.data.meetingId)}>
            View
          </Button>
        );
      }

      if (params.colDef.field === 'participants') {
        const value = params.value
          ?.map(param => {
            if (param.displayName === '' && param.email === '') {
              return param.phone;
            }

            if (param.displayName === '' || !param.displayName) {
              return param.email;
            }

            return param.displayName;
          })
          .filter(value => value)
          .join(', ');

        return <DoubleClickableCell params={params}>{value}</DoubleClickableCell>;
      }

      if (params.colDef.field === 'companies') {
        return <DoubleClickableCell params={params}>{params.data.company?.legalName}</DoubleClickableCell>;
      }

      if (params.colDef.field === 'users') {
        return (
          <DoubleClickableCell params={params}>
            {params.value.map(param => param.userName).join(', ')}
          </DoubleClickableCell>
        );
      }

      if (params.colDef.field === 'actions') {
        return (
          <IconButton
            className={style.deleteButton}
            icon="trash"
            onClick={() => onDeleteMeeting(params.data)}
            size="sm"
            title="Delete"
            variant="danger"
          />
        );
      }

      return params.value;
    },
  }));
}

function getColumnsSP({ columns, onDeleteSavedQuery, onRunQuery, onOpenReport, onRunReport }) {
  return columns.map(row => ({
    ...row.toJS(),
    editable: row.toJS().field === 'name',
    cellEditorFramework: TextEditor,
    cellClass(params) {
      if (params.colDef.field === 'actions') {
        return style.actionsCell;
      }
    },
    cellRendererFramework(params) {
      if (params.colDef.field === 'createdAt') {
        return convertDateString(params.value);
      }

      if (params.colDef.field === 'actions') {
        return (
          <>
            <div className={style.buttons}>
              <Button
                className="ml10 btn-default"
                onClick={() => onRunQuery(params.data.hash)}
                title="Open Browse/Query and run the query with the saved criteria"
              >
                Run Query
              </Button>
              <Button
                className="ml10 btn-default"
                onClick={() => onOpenReport(params.data.hash)}
                title="Run Query + “Select All” targets generated by the search and open the “Run Windward Report” popup"
              >
                Open Report
              </Button>
              {params.data.type === 'windward' && (
                <Button
                  className="ml10 btn-default"
                  onClick={() => onRunReport(params.data.hash)}
                  title="Open Report + pre-fill the fields in the “Run Windward Report” and run report generation process"
                >
                  Run Report
                </Button>
              )}
            </div>
            <IconButton
              className={style.deleteButton}
              icon="trash"
              onClick={() => onDeleteSavedQuery(params.data)}
              size="sm"
              title="Delete"
              variant="danger"
            />
          </>
        );
      }

      return params.value;
    },
  }));
}

const CheckboxCellRenderer = ({ data, onChange }) => (
  <GridCheckbox defaultChecked={data.selected} onChange={onChange} />
);

function getColumnsPD({ columns, onChangeCheckboxId }) {
  return columns.map(row => ({
    ...row.toJS(),
    cellRendererFramework(params) {
      if (params.colDef.field === 'selected') {
        return (
          <CheckboxCellRenderer data={params.data} onChange={event => onChangeCheckboxId(event, params.data, 'pd')} />
        );
      }

      return <span>{params.value}</span>;
    },
  }));
}

function renderColumns({
  name,
  columns,
  onChangeCheckboxId,
  onDeleteSavedQuery,
  onDeleteMeeting,
  onViewMeetingDetailsNote,
  onRunQuery,
  onOpenReport,
  onRunReport,
}) {
  switch (name) {
    case 'tcp':
      return getColumnsTCP({ columns });

    case 'backlog':
      return getColumnsBacklog({ columns });

    case 'meetings':
      return getColumnsMeetings({ columns, onDeleteMeeting, onViewMeetingDetailsNote });

    case 'pd':
      return getColumnsPD({ columns, onChangeCheckboxId });

    case 'sp':
      return getColumnsSP({
        columns,
        onDeleteSavedQuery,
        onRunQuery,
        onOpenReport,
        onRunReport,
      });

    default:
      return columns;
  }
}

const DashboardData = props => {
  const {
    width,
    height,
    data,
    columns,
    onGetNextPageData,
    onSort,
    onRowDoubleClick,
    onCellDoubleClick,
    name,
    clickable,
    loading,
    meta,
    serverSideSortingTables,
    onColumnResize,
    onChangeCheckboxId,
    onDeleteSavedQuery,
    onDeleteMeeting,
    onViewMeetingDetailsNote,
    onRunQuery,
    onOpenReport,
    onRunReport,
  } = props;

  const apiRef = useRef(null);

  const handleGridReady = useCallback(params => {
    apiRef.current = params.api;
  }, []);

  const handleCellValueChanged = useCallback(
    params => {
      if (params.colDef.field === 'name') {
        props.onUpdateSaveQuery(params.data);
      }
    },
    [props],
  );

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.redrawRows();
      apiRef.current.sizeColumnsToFit();
    }
  }, [loading, data]);

  const className = classNames({ clickable }, 'green');

  const rows = useMemo(
    () =>
      data.map((row, i) => {
        const updatedRow = row.set('#', i + 1);

        return <Row key={i} className={className} data={updatedRow} />;
      }),
    [data, className],
  );

  const columnsWithSortable = useMemo(
    () =>
      columns.map(column => {
        const configColumn = configColumnsMap(column.get('field'));

        if (configColumn && configColumn.has('sortable')) {
          return column.set('sortable', configColumn.get('sortable'));
        }

        return column;
      }),
    [columns],
  );

  const renderedColumns = useMemo(
    () =>
      renderColumns({
        name,
        columns: columnsWithSortable,
        onChangeCheckboxId,
        onDeleteMeeting,
        onViewMeetingDetailsNote,
        onDeleteSavedQuery,
        onRunQuery,
        onRunReport,
        onOpenReport,
      }),
    [
      name,
      columnsWithSortable,
      onChangeCheckboxId,
      onDeleteMeeting,
      onViewMeetingDetailsNote,
      onDeleteSavedQuery,
      onRunQuery,
      onRunReport,
      onOpenReport,
    ],
  );

  return (
    <Table
      api={apiRef.current}
      columns={renderedColumns}
      height={height}
      loading={loading}
      onCellDoubleClicked={meta => onCellDoubleClick(meta, name)}
      onCellValueChanged={handleCellValueChanged}
      onColumnResize={resizedColumns => onColumnResize(resizedColumns, name)}
      onGetNextPageData={onGetNextPageData}
      onGridReady={handleGridReady}
      onRowDoubleClicked={meta => onRowDoubleClick(meta, name)}
      onSortModelChanged={sortOrder => onSort(sortOrder, name)}
      page={meta?.getIn(['pagination', 'currentPage'], 0)}
      serverSideSorting={serverSideSortingTables.includes(name)}
      totalPages={meta?.getIn(['pagination', 'totalPages'], 0)}
      width={width}
      autoSizeColumnsForData
      enableSorting
      sortable
    >
      {rows}
    </Table>
  );
};

export default memo(DashboardData);
